/**
 * Scroll positions that must survive an inline editor (OWA-1345).
 *
 * EditableTable does not render its grid at all while an item is edited. The page gets much
 * shorter, so the browser clamps whichever scroller holds the table. On an invoice that is
 * an ancestor of the task table, outside anything EditableTable owns. When the editor closes the
 * grid is mounted again at full height, but nothing puts the scrollers back, so the user lands
 * somewhere else on a long page.
 *
 * `captureScroll` is called as the editor opens, before the grid unmounts. `restoreScroll` is
 * called in the commit that mounts the grid again, before the browser paints.
 */

export interface ScrollTarget {
    /** The element to scroll, or null when it is gone. A function because the grid is a new element after the editor closes. */
    resolve: () => HTMLElement | null;
    top: number;
}

/**
 * The offsets of `start` and of every ancestor that can scroll, including those at the top: a
 * user who opens an editor at offset 0 and scrolls while it is open must be returned to 0.
 */
export function captureScroll(start: HTMLElement | null): ScrollTarget[] {
    const targets: ScrollTarget[] = [];
    for (let el: HTMLElement | null = start; el; el = el.parentElement) {
        if (el.scrollTop > 0 || el.scrollHeight > el.clientHeight) {
            const node = el;
            targets.push({resolve: () => (node.isConnected ? node : null), top: node.scrollTop});
        }
    }
    return targets;
}

/** Put every target back at its saved offset. A scroller whose content is now too short ends at its new maximum. */
export function restoreScroll(targets: ScrollTarget[]): void {
    for (const target of targets) {
        const el = target.resolve();
        if (el) el.scrollTop = target.top;
    }
}
